import Field from '@atlaskit/form/field';
import TextArea from '@atlaskit/textarea/text-area';
import React, { ChangeEvent, Suspense, useState } from 'react';
import FlagGroup from '@atlaskit/flag/flag-group';
import { FormattedMessage } from 'react-intl';
import { getLocale, bitbucketCommit, bifrostEnv } from '../../config';
import { messages } from './messages';
import { FEEDBACK_CONTEXT_FIELD_ID, ENTRYPOINT_ID, SOURCE } from './constant';
import { UserAccount } from '../../clients/gateway-client/types';
import { Site } from '../../hooks/useSite';
import { isHello } from '../../utils/isHello';
import { useManifestVersion } from '../../hooks/useManifestVersion';
import { ANALYTICS_UI_SUBJECTS, ANALYTICS_SCREEN_NAMES } from '../../analytics/analyticsEvents';
import { useSendAnalyticEvent } from '../../analytics/analyticsClient';

const AkFeedbackCollector = React.lazy(
	() =>
		import(
			/* webpackChunkName: "feedback-modal/feedback-collector-component/FeedbackCollector" */ '@atlaskit/feedback-collector/FeedbackCollector'
		),
);

const AkFeedbackFlag = React.lazy(() =>
	import(
		/* webpackChunkName: "feedback-modal/feedback-flag-component/FeedbackFlag" */ '@atlaskit/feedback-collector/FeedbackFlag'
	).then((module) => ({
		default: module.FeedbackFlag,
	})),
);

export const createSummary = (feedback: string): string => {
	const summary = `[CHROME - ${bifrostEnv}] ` + feedback;
	if (summary.length >= 255) {
		return summary.substring(0, 250) + '...';
	}

	return `Environment: ${bifrostEnv}\n${summary}`;
};

export const getFeedbackContext = (
	browserManifestVersion: string,
	site: Site,
	userAccount: UserAccount,
): string => {
	const feedbackContext = {
		version: bitbucketCommit,
		release: browserManifestVersion,
		source: SOURCE,
		userId: userAccount.accountId,
		cloudId: site.workspace.cloudId,
		isHello: isHello(site.workspace.cloudId),
		environment: bifrostEnv,
	};

	return JSON.stringify(feedbackContext);
};

export interface FeedbackModalProps {
	site: Site;
	userAccount: UserAccount;
	showModal: boolean;
	setShowModal: (show: boolean) => void;
	/** Screen the modal was opened from. Used as the analytics source on submit. */
	source?: string;
	/**
	 * Renders the collector's standard form (feedback type field and opt-in checkboxes) instead of
	 * the legacy single free-text field. Gated by the feedback button placement experiment.
	 */
	showStandardForm?: boolean;
}

const FeedbackModal = ({
	site,
	userAccount,
	showModal,
	setShowModal,
	source = ANALYTICS_SCREEN_NAMES.EXTENSION_HOME_SCREEN,
	showStandardForm = false,
}: FeedbackModalProps): React.JSX.Element => {
	const { browserManifestVersion } = useManifestVersion();
	const [description, setDescription] = useState<string>('');
	const [displayFlag, setDisplayFlag] = useState(false);
	const sendAnalyticEvent = useSendAnalyticEvent();

	const hideFlag = (): void => setDisplayFlag(false);

	const onSubmit = (): void => {
		setDisplayFlag(true);

		// https://data-portal.internal.atlassian.com/analytics/registry/73850
		sendAnalyticEvent({
			eventType: 'ui',
			event: {
				action: 'submitFeedback',
				actionSubject: ANALYTICS_UI_SUBJECTS.FEEDBACK_COLLECTOR,
				source,
			},
			userId: userAccount.accountId,
		});
	};

	const close = (): void => {
		setShowModal(false);
		setDescription('');
	};

	const legacyFormProps = {
		customContent: (
			<Field
				defaultValue=""
				isRequired
				label={<FormattedMessage {...messages.feedbackCollectorLabel} />}
				name="description-field"
			>
				{({ fieldProps }: any) => (
					<TextArea
						{...fieldProps}
						minimumRows={6}
						name="feedback-collector-description"
						onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
						placeholder=""
						value={description}
					/>
				)}
			</Field>
		),
		descriptionDefaultValue: description,
		showDefaultTextFields: false,
		showTypeField: false,
		summaryDefaultValue: createSummary(description),
	};

	const standardFormProps = {
		showRequiredFieldsSummary: true,
		showTypeField: true,
	};

	return (
		<>
			{showModal && (
				<Suspense fallback={<></>}>
					<AkFeedbackCollector
						additionalFields={[
							{
								id: FEEDBACK_CONTEXT_FIELD_ID,
								value: getFeedbackContext(browserManifestVersion, site, userAccount),
							},
						]}
						atlassianAccountId={userAccount.accountId}
						email={userAccount.email}
						entrypointId={ENTRYPOINT_ID}
						locale={getLocale()}
						name={userAccount.name}
						onClose={close}
						onSubmit={onSubmit}
						{...(showStandardForm ? standardFormProps : legacyFormProps)}
					/>
				</Suspense>
			)}
			<FlagGroup onDismissed={hideFlag}>
				{displayFlag && (
					<Suspense fallback={<></>}>
						<AkFeedbackFlag locale={getLocale()} />
					</Suspense>
				)}
			</FlagGroup>
		</>
	);
};

export default FeedbackModal;
